import { Box, Toolbar } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { useRef } from 'react';
import { Outlet } from 'react-router-dom';

import { SidebarProvider } from '../context/SidebarContext';
import Breadcrumbs from '../globalComponents/BreadCrumbs';
import { FeatureErrorBoundary } from '../core-components/theme/components/FeatureErrorBoundary';
import {
  DOCK_ATTRIBUTE,
  DOCK_SCROLL_CLEARANCE,
  SHELL_BLOCK,
  shellVar,
  shellVarsSx,
} from '../core-components/theme/components/Screen';
import { QuietBannerSlot } from '../modules/close-the-loop';

import Header from './Header';
import Sidebar from './Sidebar';
import { usePageArrival } from './usePageArrival';

const shellSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', height: '100%' };

const mainSx = (theme: Theme): SystemStyleObject<Theme> => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  ...shellVarsSx(theme),
  paddingInlineStart: shellVar('inlineStart'),
  paddingInlineEnd: shellVar('inlineEnd'),
  paddingBlockStart: { xs: SHELL_BLOCK.start.xs, md: SHELL_BLOCK.start.md },
  paddingBlockEnd: shellVar('blockEnd'),
  [`&:has([${DOCK_ATTRIBUTE}])`]: { scrollPaddingBlockEnd: DOCK_SCROLL_CLEARANCE(theme) },
});

const screenSlotSx: SxProps<Theme> = { display: 'contents' };

export default function LayoutComponent(): JSX.Element {
  const screenSlot = useRef<HTMLDivElement>(null);
  usePageArrival(screenSlot);
  return (
    <SidebarProvider>
      <Box sx={shellSx}>
        <Header />
        <Toolbar />
        <Sidebar />
        <Box component="main" sx={mainSx}>
          <Box sx={{ flexShrink: 0 }}>
            <Breadcrumbs />
          </Box>
          <FeatureErrorBoundary feature="close-the-loop-quiet" fallback={null}>
            <QuietBannerSlot />
          </FeatureErrorBoundary>
          <Box ref={screenSlot} sx={screenSlotSx}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </SidebarProvider>
  );
}
